// src/app/features/orders/OrderItem/index.tsx
import React from 'react';
import _ from 'lodash';
import { IOrder } from 'app/services/responseTypes';
import {
  useRetryPaymentMutation,
  useCheckoutOrderMutation,
  useLazyGetOrdersQuery,
} from 'app/services/payments/orders';
import UserBox from 'Components/UserBox';
import Button from 'Components/Buttons/Button';
import Tooltip from 'Components/Tooltips';
import { USDate } from 'Components/Dates/USDate';
import useCreateModal from 'Components/Popups/useCreateModal';
import ServiceAgreementModal from '../../services/ServiceAgreementModal';
// Import the auth hook to get the currently logged in user
import { useAuth } from 'utils/AuthProvider';

export interface IOrderItemProps {
  order: IOrder;
}

export default function OrderItem({ order: initialOrder }: IOrderItemProps) {
  const [retryPayment, { isLoading: isRetryLoading }] =
    useRetryPaymentMutation();
  const [checkoutOrder, { isLoading: isCheckoutLoading }] =
    useCheckoutOrderMutation();
  const [triggerGetOrders] = useLazyGetOrdersQuery();

  // Get the current user from auth context.
  const { user: currentUser } = useAuth();

  const AgreementModal = useCreateModal();
  const paymentMode = React.useRef<'checkout' | 'retry'>();
  const isServiceOrder = initialOrder.order_items.some(
    (oi) =>
      oi.product_meta.type === 'service' || oi.product_meta.type === 'bundle',
  );

  const openAgreement = (mode: 'checkout' | 'retry') => {
    paymentMode.current = mode;
    AgreementModal.open();
  };

  const proceedPayment = () => {
    if (paymentMode.current) handlePayment(paymentMode.current);
  };

  /* ---------- total amounts ---------- */
  const totalAmount =
    initialOrder.order_items.reduce(
      (sum, item) => sum + item.price_meta.amount_cents,
      0,
    ) / 100;
  const paidAmount =
    initialOrder.order_items.reduce(
      (sum, item) =>
        sum + (item.price_meta.paid_cents ?? item.price_meta.amount_cents),
      0,
    ) / 100;
  const hasNoLineItems =
    !initialOrder.order_items || initialOrder.order_items.length === 0;
  const isZeroTotal = initialOrder.total_price === 0;

  /* ---------- payment flow ---------- */
  async function handlePayment(mode: 'checkout' | 'retry') {
    if (isZeroTotal) {
      alert('Total price is $0. Cannot proceed.');
      return;
    }

    let order = initialOrder;
    if (hasNoLineItems) {
      try {
        const refreshed = await triggerGetOrders({
          filters: { id: order.id },
        }).unwrap();
        if (refreshed.data?.[0]) order = refreshed.data[0];
      } catch (err) {
        console.error('Order refresh failed', err);
      }
    }

    try {
      let res: any;
      if (mode === 'checkout') {
        res = await checkoutOrder(order.id).unwrap();
      } else {
        res = await retryPayment({ orderId: order.id }).unwrap();
      }
      if (res?.redirect_url) window.location.replace(res.redirect_url);
    } catch (err: any) {
      alert(err.data?.message ?? 'Payment error');
    }
  }

  return (
    <div className="w-2/5 md:w-2/5 md-max:w-full rounded-3xl shadow-all grid gap-2 pb-4">
      {/* -- header: user + status + buttons -- */}
      <div className="border-b px-4 py-2 text-gray-dark text-sm border-gray-100 grid gap-2">
        {initialOrder.user && (
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-2">
            {/* Left side: user info */}
            <UserBox user={initialOrder.user} size="xs" theme="white" />

            {/* Right side: status label and payment button, but show buttons only if current user's ID matches */}
            <div className="flex flex-col items-start md:items-end gap-2">
              <div
                className={`text-xs capitalize px-2 py-1 rounded-lg w-max font-bold ${
                  initialOrder.state === 'completed'
                    ? 'text-green'
                    : 'text-red-dark'
                }`}
              >
                {initialOrder.state.replace('_', ' ')}
              </div>

              {/* Only show payment actions if the order belongs to the current user */}
              {currentUser &&
                currentUser.id === initialOrder.user.id &&
                initialOrder.state === 'pending_payment' && (
                  <Button
                    theme="green-fill"
                    isLoading={isCheckoutLoading}
                    onClick={() =>
                      isServiceOrder
                        ? openAgreement('checkout')
                        : handlePayment('checkout')
                    }
                  >
                    Pay Now
                  </Button>
                )}

              {currentUser &&
                currentUser.id === initialOrder.user.id &&
                initialOrder.state === 'processing_payment' && (
                  <Button
                    theme="green-fill"
                    isLoading={isRetryLoading}
                    onClick={() =>
                      isServiceOrder
                        ? openAgreement('retry')
                        : handlePayment('retry')
                    }
                  >
                    Retry Payment
                  </Button>
                )}
            </div>
          </div>
        )}
      </div>

      {/* Grouped line‑items by business */}
      {initialOrder.order_items &&
        Object.entries(
          _.groupBy(
            initialOrder.order_items,
            (o) => o.details?.business?.name || '',
          ),
        ).map(([businessName, orderItems]) => (
          <div className="border-b border-gray-100 px-4" key={businessName}>
            {businessName && businessName !== 'personal_business' && (
              <span className="text-sm text-gray-dark font-semibold">
                {businessName}
              </span>
            )}
            {orderItems.map((o) => (
              <div className="text-sm text-gray-700 pl-1 pb-1" key={o.id}>
                {o.product_meta.type === 'project' && (
                  <div className="text-sm text-gray-700">Tax Return</div>
                )}
                <span className="capitalize flex gap-4 items-center">
                  <span className="text-gray-dark">{o.product_meta.title}</span>
                  <span className="ml-auto shadow rounded-md px-2 py-1">
                    {o.price_meta.type}
                  </span>
                  <span className="text-green font-bold">
                    ${o.price_meta.amount_cents / 100}
                  </span>
                </span>
                {o.product_meta.description && (
                  <span className="ml-2">{o.product_meta.description}</span>
                )}
              </div>
            ))}
          </div>
        ))}

      {/* Discount row (if any) */}
      {totalAmount !== paidAmount && (
        <div className="flex px-4 text-gray-700 text-sm justify-between">
          Discount
          <span className="text-red-200">
            -${(totalAmount - paidAmount).toFixed(2)}
          </span>
        </div>
      )}

      {/* Total */}
      <div className="flex px-4 text-base font-bold text-gray-700 justify-between">
        Total
        <span className="text-green">
          ${(initialOrder.total_price / 100).toFixed(2)}
        </span>
      </div>

      {/* Meta info */}
      <div className="max-w-min">
        <Tooltip message="Order ID">
          <span className="px-4 text-xs text-gray-700 select-all">
            {initialOrder.id}
          </span>
        </Tooltip>
      </div>
      <USDate
        className="text-sm text-gray-700 px-4"
        date={initialOrder.created_at}
      />
      <AgreementModal.Component>
        <ServiceAgreementModal onAgree={proceedPayment} />
      </AgreementModal.Component>
    </div>
  );
}
